import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useBilling } from "@/hooks/useBilling";
import { STATUS_LABELS, formatRenewalDate } from "@/lib/billing";
import { tierLabel } from "@/lib/billingConfig";

/**
 * Billing settings.
 *
 * After a checkout the browser returns here with `?checkout=complete`. That is
 * NOT proof of payment — it only starts a short poll while we wait for the
 * verified webhook to grant the entitlement.
 */
export default function Billing() {
  const { billing, loading, refresh } = useBilling();
  const [params, setParams] = useSearchParams();
  const [awaiting, setAwaiting] = useState(params.get("checkout") === "complete");
  const attempts = useRef(0);

  useEffect(() => {
    if (!awaiting) return;
    if (billing?.canUsePaidFeatures) {
      setAwaiting(false);
      params.delete("checkout");
      setParams(params, { replace: true });
      return;
    }
    if (attempts.current >= 10) {
      setAwaiting(false);
      return;
    }
    const t = window.setTimeout(() => {
      attempts.current += 1;
      void refresh();
    }, 3000);
    return () => window.clearTimeout(t);
  }, [awaiting, billing, refresh, params, setParams]);

  const sub = billing?.subscription ?? null;
  const ent = billing?.entitlement ?? null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Billing — Family Loan Planner™</title>
        <meta name="description" content="View your Family Loan Planner plan, billing interval, subscription status and renewal date." />
      </Helmet>

      <main className="container mx-auto px-4 py-10 max-w-3xl">
        <h1 className="text-2xl font-bold tracking-tight mb-6">Billing</h1>

        {awaiting && (
          <Card className="p-4 mb-6 border-primary/40 bg-primary/5">
            <p className="text-sm font-medium">Confirming your payment…</p>
            <p className="text-sm text-muted-foreground mt-1">
              We're waiting for confirmation from the payment provider. This usually takes a few
              seconds. You don't need to pay again.
            </p>
          </Card>
        )}

        {billing?.isReadOnly && (
          <Card className="p-4 mb-6 border-amber-500/40 bg-amber-500/5">
            <p className="text-sm font-medium">Your plan has lapsed — your data is safe</p>
            <p className="text-sm text-muted-foreground mt-1">
              Nothing has been deleted. You can still open, print and export every scenario,
              commitment and report. Creating new paid-only records is paused until the plan is
              renewed.
            </p>
          </Card>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading your billing details…</p>
        ) : (
          <Card className="p-6">
            <dl className="grid gap-4 sm:grid-cols-2 text-sm">
              <div>
                <dt className="text-muted-foreground">Plan</dt>
                <dd className="font-medium mt-0.5">
                  {ent?.tier ? tierLabel(ent.tier) : ent?.plan === "beta" ? "Beta access" : "Free"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Status</dt>
                <dd className="mt-0.5 flex items-center gap-2">
                  {sub ? (
                    <Badge variant={sub.status === "active" ? "default" : "secondary"}>
                      {STATUS_LABELS[sub.status]}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">No subscription</Badge>
                  )}
                  {billing?.inGrace && <span className="text-xs text-muted-foreground">grace period</span>}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Billing interval</dt>
                <dd className="font-medium mt-0.5">
                  {sub ? (sub.bill_interval === "year" ? "Annual" : "Monthly") : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">
                  {sub?.scheduled_cancel_at ? "Access ends" : "Next renewal"}
                </dt>
                <dd className="font-medium mt-0.5">
                  {formatRenewalDate(sub?.scheduled_cancel_at ?? sub?.current_period_end ?? null)}
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-3 mt-6">
              {sub?.management_url && (
                <Button asChild variant="outline">
                  <a href={sub.management_url} target="_blank" rel="noopener noreferrer">
                    Update payment method
                  </a>
                </Button>
              )}
              {sub?.cancel_url && (
                <Button asChild variant="outline">
                  <a href={sub.cancel_url} target="_blank" rel="noopener noreferrer">
                    Manage or cancel
                  </a>
                </Button>
              )}
              <Button asChild>
                <Link to="/pricing">{sub ? "Change plan" : "View plans"}</Link>
              </Button>
            </div>

            {!sub?.management_url && (
              <p className="text-xs text-muted-foreground mt-4">
                A self-serve management link appears here once your subscription is active.
              </p>
            )}
          </Card>
        )}
      </main>
    </div>
  );
}
